// Utilidades do "Trabalhe Conosco em etapas": modelo dos passos, validação, máscaras,
// persistência e as chamadas ao relay (contrato §3). Sem React e sem DOM no topo do
// módulo — o que toca navegador (sessionStorage, XMLHttpRequest) roda só dentro de
// funções chamadas por eventos/efeitos do cliente.
import type { TcCampo, TcConfig, TcEtapa, TcRespostaErro, TcRespostas, TcValorResposta } from '../../lib/tc/types';
import { emailValido, mascaraTelefone, soDigitos } from '../avm/avm.utils';

export { emailValido, mascaraTelefone, soDigitos };

/* ── modelo dos passos ──────────────────────────────────────────────────────── */

export type TcPassoDef =
    | { tipo: 'dados'; titulo: string; ajuda: string }
    | { tipo: 'codigo'; titulo: string; ajuda: string }
    | { tipo: 'curriculo'; titulo: string; ajuda: string }
    | { tipo: 'etapa'; titulo: string; ajuda: string; etapa: TcEtapa }
    | { tipo: 'revisao'; titulo: string; ajuda: string };

export function montarPassos(config: TcConfig): TcPassoDef[] {
    const passos: TcPassoDef[] = [
        { tipo: 'dados', titulo: 'Seus dados', ajuda: 'Como podemos te chamar e falar com você.' },
        { tipo: 'codigo', titulo: 'Confirme seu e-mail', ajuda: 'Enviamos um código de 6 dígitos. Ele vale por alguns minutos.' },
    ];
    if (config.curriculo.ativo) {
        passos.push({
            tipo: 'curriculo',
            titulo: 'Seu currículo',
            ajuda: config.curriculo.obrigatorio
                ? 'Envie o seu currículo em PDF (até 5 MB).'
                : 'Se quiser, envie o seu currículo em PDF (até 5 MB). Você pode pular esta etapa.',
        });
    }
    for (const etapa of config.etapas) {
        passos.push({ tipo: 'etapa', titulo: etapa.titulo, ajuda: etapa.ajuda, etapa });
    }
    passos.push({ tipo: 'revisao', titulo: 'Revisar e enviar', ajuda: 'Confira os seus dados antes de enviar a candidatura.' });
    return passos;
}

export const INDICE_DADOS = 0;
export const INDICE_CODIGO = 1;

/** índice do passo onde um campo (reservado ou das etapas) é respondido; -1 = não achou. */
export function passoDoCampo(passos: TcPassoDef[], chave: string): number {
    if (['nome', 'email', 'celular', 'cidade', 'vagas'].indexOf(chave) !== -1) return INDICE_DADOS;
    for (let i = 0; i < passos.length; i += 1) {
        const p = passos[i];
        if (p.tipo === 'etapa' && p.etapa.campos.some((c) => c.key === chave)) return i;
    }
    return -1;
}

/* ── estado persistido ──────────────────────────────────────────────────────── */

export interface TcDados {
    nome: string;
    email: string;
    celular: string;
    cidade: string;
    vagas: number[];
}

export interface TcCurriculoEnviado {
    nome: string;
    bytes: number;
}

export interface TcEstado {
    passo: number;
    dados: TcDados;
    respostas: TcRespostas;
    verificacaoId: string | null;
    emailMascarado: string | null;
    tcToken: string | null;
    /** epoch ms em que o tcToken deixa de valer */
    tcExpiraEm: number | null;
    curriculo: TcCurriculoEnviado | null;
    /** foto dos dados que geraram a verificação atual: mudou nome/e-mail/celular → verifica de novo. */
    dadosVerificados: TcDados | null;
    consentimento: boolean;
}

export const TC_DADOS_INICIAIS: TcDados = { nome: '', email: '', celular: '', cidade: '', vagas: [] };

export const TC_ESTADO_INICIAL: TcEstado = {
    passo: 0,
    dados: TC_DADOS_INICIAIS,
    respostas: {},
    verificacaoId: null,
    emailMascarado: null,
    tcToken: null,
    tcExpiraEm: null,
    curriculo: null,
    dadosVerificados: null,
    consentimento: false,
};

/** nome/e-mail/celular/cidade iguais aos que geraram a verificação? (vagas não contam) */
export function mesmosDados(a: TcDados | null, b: TcDados | null): boolean {
    if (!a || !b) return false;
    const norm = (v: string) => String(v || '').trim().toLowerCase();
    return norm(a.nome) === norm(b.nome) && norm(a.email) === norm(b.email) && soDigitos(a.celular) === soDigitos(b.celular) && norm(a.cidade) === norm(b.cidade);
}

export function chaveStorage(config: TcConfig): string {
    return `tc:${config.codsite}:${config.pageId ?? 0}:${config.blockId ?? ''}`;
}

/** Token ainda vale? (com 30 s de folga para a chamada chegar ao app) */
export function tokenValido(estado: Pick<TcEstado, 'tcToken' | 'tcExpiraEm'>, agora = Date.now()): boolean {
    return !!estado.tcToken && (!estado.tcExpiraEm || estado.tcExpiraEm - 30000 > agora);
}

/**
 * Lê o progresso salvo (sessionStorage) e o sanea contra o config ATUAL: passo dentro da
 * faixa, token expirado descartado, e nunca cai num passo que exige o que não tem.
 */
export function restaurarEstado(chave: string, totalPassos: number, agora = Date.now()): TcEstado | null {
    let cru: unknown = null;
    try {
        const texto = window.sessionStorage.getItem(chave);
        cru = texto ? JSON.parse(texto) : null;
    } catch {
        return null;
    }
    if (!cru || typeof cru !== 'object') return null;
    const o = cru as Record<string, any>;
    const dadosCru = o.dados && typeof o.dados === 'object' ? o.dados : {};
    const estado: TcEstado = {
        passo: Number.isInteger(o.passo) ? Math.max(0, Math.min(totalPassos - 1, o.passo)) : 0,
        dados: {
            nome: typeof dadosCru.nome === 'string' ? dadosCru.nome : '',
            email: typeof dadosCru.email === 'string' ? dadosCru.email : '',
            celular: typeof dadosCru.celular === 'string' ? dadosCru.celular : '',
            cidade: typeof dadosCru.cidade === 'string' ? dadosCru.cidade : '',
            vagas: Array.isArray(dadosCru.vagas) ? dadosCru.vagas.filter((v: unknown) => Number.isInteger(v)) : [],
        },
        respostas: o.respostas && typeof o.respostas === 'object' && !Array.isArray(o.respostas) ? o.respostas : {},
        verificacaoId: normalizarVerificacaoId(o.verificacaoId),
        emailMascarado: typeof o.emailMascarado === 'string' && o.emailMascarado ? o.emailMascarado : null,
        tcToken: typeof o.tcToken === 'string' && o.tcToken ? o.tcToken : null,
        tcExpiraEm: Number.isFinite(o.tcExpiraEm) ? Number(o.tcExpiraEm) : null,
        curriculo:
            o.curriculo && typeof o.curriculo === 'object' && typeof o.curriculo.nome === 'string'
                ? { nome: o.curriculo.nome, bytes: Number(o.curriculo.bytes) || 0 }
                : null,
        dadosVerificados:
            o.dadosVerificados && typeof o.dadosVerificados === 'object'
                ? {
                      nome: String(o.dadosVerificados.nome || ''),
                      email: String(o.dadosVerificados.email || ''),
                      celular: String(o.dadosVerificados.celular || ''),
                      cidade: String(o.dadosVerificados.cidade || ''),
                      vagas: [],
                  }
                : null,
        consentimento: false, // o consentimento é sempre marcado de novo na hora de enviar
    };
    if (!tokenValido(estado, agora)) {
        estado.tcToken = null;
        estado.tcExpiraEm = null;
        estado.curriculo = null; // o currículo fica ligado à verificação; sem token, envia de novo
        estado.dadosVerificados = null;
        if (estado.passo > INDICE_CODIGO) estado.passo = estado.verificacaoId ? INDICE_CODIGO : INDICE_DADOS;
    } else if (estado.passo === INDICE_CODIGO) {
        estado.passo = INDICE_CODIGO + 1; // já verificado: não pede o código de novo
    }
    if (estado.passo === INDICE_CODIGO && !estado.verificacaoId) estado.passo = INDICE_DADOS;
    return estado;
}

export function salvarEstado(chave: string, estado: TcEstado): void {
    try {
        window.sessionStorage.setItem(chave, JSON.stringify({ ...estado, consentimento: false, salvoEm: Date.now() }));
    } catch {
        /* sessionStorage cheia ou bloqueada: a tela segue funcionando sem persistência */
    }
}

export function limparEstado(chave: string): void {
    try {
        window.sessionStorage.removeItem(chave);
        window.sessionStorage.removeItem(`${chave}:t0`);
    } catch {
        /* idem */
    }
}

/** `verificacaoId` pode vir string ou número (contrato §6): normaliza com String(); vazio/inválido = null. */
export function normalizarVerificacaoId(v: unknown): string | null {
    if (typeof v === 'number' && !Number.isFinite(v)) return null;
    if (typeof v !== 'string' && typeof v !== 'number') return null;
    const t = String(v).trim();
    return t ? t : null;
}

/** ms desde o 1º render, só no relógio do navegador (contrato §6.2). Nunca negativo. */
export function decorridoDesde(t0Local: number, agora = Date.now()): number {
    return Math.max(0, Math.round(agora - t0Local));
}

/** 401 do token do site: confere o status ANTES do `erro` (contrato §6.3). */
export function ehNaoAutorizado(status: number, json: unknown): boolean {
    return status === 401 || codigoDoErro(json) === 'NAO_AUTORIZADO';
}

export const MENSAGEM_INDISPONIVEL = 'Formulário indisponível no momento';
export const MENSAGEM_DUPLICADO =
    'Já recebemos uma candidatura com este e-mail ou celular. Se precisar atualizar algo, fale com a imobiliária.';

/**
 * `t0` do contrato = ms do PRIMEIRO render da página para esta pessoa. Fica na
 * sessionStorage: quem recarrega com os dados já preenchidos e clica em menos de 3 s
 * não pode cair na heurística de robô do app.
 */
export function lerOuGravarT0(chave: string, agora = Date.now()): number {
    const k = `${chave}:t0`;
    try {
        const salvo = Number(window.sessionStorage.getItem(k));
        if (Number.isFinite(salvo) && salvo > 0 && salvo <= agora) return salvo;
        window.sessionStorage.setItem(k, String(agora));
    } catch {
        /* sem storage: vale o render atual */
    }
    return agora;
}

/* ── máscaras e validações ──────────────────────────────────────────────────── */

export function mascaraCpf(valor: string): string {
    const d = soDigitos(valor).slice(0, 11);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export function mascaraCnpj(valor: string): string {
    const d = soDigitos(valor).slice(0, 14);
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
    if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
    if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
    return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export function mascaraData(valor: string): string {
    const d = soDigitos(valor).slice(0, 8);
    if (d.length <= 2) return d;
    if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
    return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

export function cpfValido(valor: string): boolean {
    const d = soDigitos(valor);
    if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
    const dv = (tam: number) => {
        let soma = 0;
        for (let i = 0; i < tam; i += 1) soma += parseInt(d.charAt(i), 10) * (tam + 1 - i);
        const resto = (soma * 10) % 11;
        return resto === 10 ? 0 : resto;
    };
    return dv(9) === parseInt(d.charAt(9), 10) && dv(10) === parseInt(d.charAt(10), 10);
}

export function cnpjValido(valor: string): boolean {
    const d = soDigitos(valor);
    if (d.length !== 14 || /^(\d)\1{13}$/.test(d)) return false;
    const dv = (tam: number) => {
        const pesos = tam === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
        let soma = 0;
        for (let i = 0; i < tam; i += 1) soma += parseInt(d.charAt(i), 10) * pesos[i];
        const resto = soma % 11;
        return resto < 2 ? 0 : 11 - resto;
    };
    return dv(12) === parseInt(d.charAt(12), 10) && dv(13) === parseInt(d.charAt(13), 10);
}

export function dataValida(valor: string): boolean {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(valor || '').trim());
    if (!m) return false;
    const dia = parseInt(m[1], 10);
    const mes = parseInt(m[2], 10);
    const ano = parseInt(m[3], 10);
    if (ano < 1900 || ano > 2100 || mes < 1 || mes > 12 || dia < 1) return false;
    const ultimo = new Date(ano, mes, 0).getDate();
    return dia <= ultimo;
}

export function celularValido(valor: string): boolean {
    const d = soDigitos(valor);
    return d.length === 10 || d.length === 11;
}

/** aplica a máscara do `validate` do campo (short) enquanto digita. */
export function aplicarMascara(campo: TcCampo, valor: string): string {
    if (campo.type !== 'short') return valor;
    switch (campo.validate) {
        case 'telefone':
            return mascaraTelefone(valor);
        case 'cpf':
            return mascaraCpf(valor);
        case 'cnpj':
            return mascaraCnpj(valor);
        case 'data':
            return mascaraData(valor);
        default:
            return valor;
    }
}

export const TC_MAX_TEXTO_LONGO = 2000;
export const TC_MAX_TEXTO_CURTO = 200;

export type TcErros = Record<string, string>;

export function validarDados(dados: TcDados): TcErros {
    const erros: TcErros = {};
    if (dados.nome.trim().length < 3) erros.nome = 'Informe o seu nome completo.';
    if (!emailValido(dados.email)) erros.email = 'Informe um e-mail válido — o código de confirmação vai para ele.';
    if (!celularValido(dados.celular)) erros.celular = 'Informe o celular com DDD.';
    return erros;
}

export function validarCampo(campo: TcCampo, valor: TcValorResposta | undefined): string | null {
    const vazio = valor == null || (Array.isArray(valor) ? valor.length === 0 : !String(valor).trim());
    if (vazio) return campo.required ? 'Este campo é obrigatório.' : null;
    if (campo.type !== 'short' || Array.isArray(valor)) return null;
    const texto = String(valor).trim();
    switch (campo.validate) {
        case 'email':
            return emailValido(texto) ? null : 'Informe um e-mail válido.';
        case 'telefone':
            return celularValido(texto) ? null : 'Informe o telefone com DDD.';
        case 'cpf':
            return cpfValido(texto) ? null : 'CPF inválido.';
        case 'cnpj':
            return cnpjValido(texto) ? null : 'CNPJ inválido.';
        case 'data':
            return dataValida(texto) ? null : 'Use o formato dd/mm/aaaa.';
        default:
            return null;
    }
}

export function validarEtapa(etapa: TcEtapa, respostas: TcRespostas): TcErros {
    const erros: TcErros = {};
    for (const campo of etapa.campos) {
        const erro = validarCampo(campo, respostas[campo.key]);
        if (erro) erros[campo.key] = erro;
    }
    return erros;
}

/** nº de colunas (de 12) que o campo ocupa no desktop a partir do `width` em %. */
export function colunasDoCampo(campo: TcCampo): number {
    if (!campo.width) return 12;
    return Math.max(3, Math.min(12, Math.round((campo.width / 100) * 12)));
}

export function formatarBytes(bytes: number): string {
    if (!Number.isFinite(bytes) || bytes <= 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

export const TC_MAX_PDF_BYTES = 5 * 1024 * 1024;

/** Confere extensão, tamanho e os 5 primeiros bytes (`%PDF-`) antes de subir o arquivo. */
export function conferirPdf(arquivo: File): Promise<string | null> {
    if (!/\.pdf$/i.test(arquivo.name || '')) return Promise.resolve('Envie um arquivo PDF.');
    if (arquivo.size > TC_MAX_PDF_BYTES) return Promise.resolve('O arquivo passa de 5 MB. Reduza o PDF e tente de novo.');
    if (arquivo.size < 5) return Promise.resolve('O arquivo está vazio.');
    return new Promise((resolve) => {
        try {
            const leitor = new FileReader();
            leitor.onerror = () => resolve('Não conseguimos ler o arquivo. Tente de novo.');
            leitor.onload = () => {
                const bytes = new Uint8Array((leitor.result as ArrayBuffer) || new ArrayBuffer(0));
                let assinatura = '';
                for (let i = 0; i < bytes.length; i += 1) assinatura += String.fromCharCode(bytes[i]);
                resolve(assinatura === '%PDF-' ? null : 'Esse arquivo não é um PDF válido.');
            };
            leitor.readAsArrayBuffer(arquivo.slice(0, 5));
        } catch {
            resolve('Não conseguimos ler o arquivo. Tente de novo.');
        }
    });
}

/* ── chamadas ao relay ──────────────────────────────────────────────────────── */

export interface TcAutenticacao {
    codsite: number;
    siteToken: string;
    tcToken?: string | null;
}

export interface TcRetorno<T> {
    status: number;
    json: T | TcRespostaErro | null;
}

function cabecalhos(auth: TcAutenticacao, extras: Record<string, string> = {}): Record<string, string> {
    const h: Record<string, string> = {
        'x-tc-codsite': String(auth.codsite),
        'x-tc-site-token': auth.siteToken,
        ...extras,
    };
    if (auth.tcToken) h['x-tc-token'] = auth.tcToken;
    return h;
}

export async function chamarTc<T>(
    acao: 'vagas' | 'iniciar' | 'reenviar' | 'verificar' | 'enviar',
    auth: TcAutenticacao,
    corpo?: Record<string, unknown>,
): Promise<TcRetorno<T>> {
    const res = await fetch(`/api/trabalhe-conosco/${acao}`, {
        method: acao === 'vagas' ? 'GET' : 'POST',
        headers: cabecalhos(auth, acao === 'vagas' ? {} : { 'content-type': 'application/json' }),
        body: acao === 'vagas' ? undefined : JSON.stringify(corpo || {}),
        cache: 'no-store',
    });
    const json = (await res.json().catch(() => null)) as T | TcRespostaErro | null;
    return { status: res.status, json };
}

/** Upload do currículo com progresso (XMLHttpRequest: fetch não expõe `upload.onprogress`). */
export function enviarCurriculo<T>(
    arquivo: File,
    auth: TcAutenticacao,
    aoProgredir: (_pct: number) => void,
): { promessa: Promise<TcRetorno<T>>; cancelar: () => void } {
    const xhr = new XMLHttpRequest();
    const promessa = new Promise<TcRetorno<T>>((resolve, reject) => {
        xhr.open('POST', '/api/trabalhe-conosco/curriculo', true);
        const h = cabecalhos(auth);
        for (const nome of Object.keys(h)) xhr.setRequestHeader(nome, h[nome]);
        xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable && ev.total > 0) aoProgredir(Math.min(99, Math.round((ev.loaded / ev.total) * 100)));
        };
        xhr.onerror = () => reject(new Error('rede'));
        xhr.onabort = () => reject(new Error('abortado'));
        xhr.ontimeout = () => reject(new Error('timeout'));
        xhr.onload = () => {
            let json: any = null;
            try {
                json = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch {
                json = null;
            }
            resolve({ status: xhr.status, json });
        };
        xhr.timeout = 120000;
        const dados = new FormData();
        dados.append('file', arquivo, arquivo.name);
        xhr.send(dados);
    });
    return { promessa, cancelar: () => xhr.abort() };
}

/* ── mensagens dos erros do contrato ────────────────────────────────────────── */

export function codigoDoErro(json: unknown): string {
    if (!json || typeof json !== 'object') return '';
    const o = json as Record<string, unknown>;
    return o.ok === false && typeof o.erro === 'string' ? o.erro : '';
}

export function mensagemDoErro(json: unknown, padrao: string): string {
    const codigo = codigoDoErro(json);
    const doApp = json && typeof json === 'object' && typeof (json as any).mensagem === 'string' ? String((json as any).mensagem).trim() : '';
    switch (codigo) {
        case 'DUPLICADO':
            return MENSAGEM_DUPLICADO;
        case 'LIMITE':
            return doApp || 'Muitas tentativas agora há pouco. Aguarde alguns minutos e tente de novo.';
        case 'CODIGO_INVALIDO':
            return 'Código incorreto. Confira os 6 dígitos e tente de novo.';
        case 'CODIGO_EXPIRADO':
            return 'Esse código expirou. Peça um novo código abaixo.';
        case 'TENTATIVAS':
            return 'Muitas tentativas com esse código. Peça um novo código abaixo.';
        case 'TOKEN_INVALIDO':
            return 'Sua sessão expirou. Confirme o seu e-mail de novo para continuar.';
        case 'PDF_INVALIDO':
            return 'Esse arquivo não é um PDF válido.';
        case 'PDF_GRANDE':
            return 'O arquivo passa de 5 MB. Reduza o PDF e tente de novo.';
        case 'VALIDACAO':
            return doApp || 'Alguns campos precisam de atenção.';
        case 'INDISPONIVEL':
            return doApp || 'As candidaturas estão temporariamente indisponíveis.';
        default:
            return doApp || padrao;
    }
}

/** `campos` da resposta VALIDACAO do app → só pares string/string. */
export function camposDoErro(json: unknown): TcErros {
    const saida: TcErros = {};
    if (!json || typeof json !== 'object') return saida;
    const campos = (json as any).campos;
    if (!campos || typeof campos !== 'object') return saida;
    for (const k of Object.keys(campos)) {
        if (typeof campos[k] === 'string' && campos[k].trim()) saida[k] = campos[k].trim();
    }
    return saida;
}

/** nome só com o primeiro nome, para a tela de sucesso. */
export function primeiroNome(nome: string): string {
    const limpo = String(nome || '').trim().split(/\s+/)[0] || '';
    return limpo ? limpo.charAt(0).toUpperCase() + limpo.slice(1) : '';
}
